import { Download, ExternalLink, ArrowRight, MessageCircle } from "lucide-react";
import { site, waLink } from "@/lib/site";
import { track } from "@/lib/analytics";

export default function Segmentos() {
  const waFor = (title) =>
    `https://wa.me/${site.whatsappRaw}?text=${encodeURIComponent(
      `Olá! Tenho interesse na linha ${title} da Serequímica. Pode me passar mais detalhes?`
    )}`;

  return (
    <section
      id="segmentos"
      data-testid="segmentos-section"
      className="relative bg-[#f5efe1] py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <div className="text-[#8a6b26] uppercase tracking-[0.25em] text-xs font-semibold">
            Nossos catálogos
          </div>
          <h2
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0e3b21] leading-tight"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Três segmentos.<br />Uma química que <em className="text-[#8a6b26]">performa</em>.
          </h2>
          <div className="gold-underline mt-5" />
          <p className="mt-6 text-[#1a3a26]/80 text-base sm:text-lg leading-relaxed">
            Cada catálogo foi montado para o dia a dia real da operação — com
            fichas técnicas, modo de uso e a garantia do padrão Serequímica.
            Baixe agora ou fale com nosso time para uma indicação personalizada.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {site.catalogs.map((c, idx) => (
            <article
              key={c.id}
              data-testid={`catalog-card-${c.id}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(14,59,33,0.08)] hover:shadow-[0_18px_50px_rgba(14,59,33,0.18)] transition-all duration-500 border border-[#0e3b21]/5 flex flex-col"
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-[#0a2a17]">
                <img
                  src={c.cover}
                  alt={`Catálogo ${c.title}`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  loading={idx > 0 ? "lazy" : "eager"}
                />
                <div className="absolute top-3 left-3 bg-[#c9a24c] text-[#0e3b21] text-[10px] font-bold px-3 py-1 rounded-full tracking-widest uppercase">
                  Catálogo {idx + 1}
                </div>
              </div>

              <div className="p-6 sm:p-7 flex flex-col flex-1">
                <h3
                  className="text-xl sm:text-2xl font-bold text-[#0e3b21]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  {c.title}
                </h3>
                <p className="mt-1 text-sm text-[#8a6b26] italic">
                  {c.subtitle}
                </p>
                <p className="mt-4 text-sm text-[#1a3a26]/80 leading-relaxed flex-1">
                  {c.description}
                </p>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {c.badges.map((b) => (
                    <span
                      key={b}
                      className="text-[10px] uppercase tracking-wider font-semibold text-[#0e3b21] bg-[#c9a24c]/20 border border-[#c9a24c]/40 rounded-full px-2.5 py-1"
                    >
                      {b}
                    </span>
                  ))}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-2">
                  <a
                    data-testid={`catalog-download-${c.id}`}
                    href={c.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track("catalog_download", { catalog: c.id, title: c.title })}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#0e3b21] hover:bg-[#0a2a17] text-white font-semibold px-4 py-3 text-sm transition-all"
                  >
                    <Download size={15} strokeWidth={2.4} />
                    Baixar catálogo
                  </a>
                  <a
                    data-testid={`catalog-wa-${c.id}`}
                    href={waFor(c.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track("whatsapp_click", { source: "catalog_card", catalog: c.id })}
                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0e3b21]/15 hover:border-[#25D366] hover:text-[#25D366] text-[#0e3b21] font-semibold px-4 py-3 text-sm transition-all"
                    aria-label="Falar sobre esta linha no WhatsApp"
                  >
                    <MessageCircle size={15} strokeWidth={2.4} />
                  </a>
                </div>

                <a
                  href={c.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("catalog_view_online", { catalog: c.id })}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#8a6b26] hover:text-[#0e3b21] font-medium"
                >
                  Visualizar online <ExternalLink size={12} />
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <a
            data-testid="segmentos-cta"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#0e3b21] hover:text-[#8a6b26] font-semibold text-base"
          >
            Não achou o que precisa? Fale com um especialista{" "}
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}
